import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useOpenedGoal } from "@/lib/metrica";
import { fmtTRY } from "@/lib/format";
import { PageHeader, SectionCard, StatCard, EmptyState, StickyPageHead } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { toast } from "sonner";
import ExpenseReports from "@/pages/ExpenseReports";
import VatReport from "@/pages/VatReport";

const pad = (n) => String(n).padStart(2, "0");
const lastDayOfMonth = (y, m0) => new Date(y, m0 + 1, 0).getDate();
function presetRange(preset) {
  const t = new Date();
  const y = t.getFullYear(); const m = t.getMonth();
  if (preset === "month") {
    return { start: `${y}-${pad(m + 1)}-01`, end: `${y}-${pad(m + 1)}-${pad(lastDayOfMonth(y, m))}` };
  }
  if (preset === "lastMonth") {
    const lm = new Date(y, m - 1, 1);
    const ly = lm.getFullYear(); const lmi = lm.getMonth();
    return { start: `${ly}-${pad(lmi + 1)}-01`, end: `${ly}-${pad(lmi + 1)}-${pad(lastDayOfMonth(ly, lmi))}` };
  }
  if (preset === "year") {
    return { start: `${y}-01-01`, end: `${y}-12-31` };
  }
  return { start: "", end: "" };
}
const PRESETS = [
  { key: "month", label: "Bu Ay" },
  { key: "lastMonth", label: "Geçen Ay" },
  { key: "year", label: "Bu Yıl" },
  { key: "custom", label: "Özel" },
];

const INITIAL_MONTH = presetRange("month");
const EMPTY_FILTERS = {
  start_date: INITIAL_MONTH.start, end_date: INITIAL_MONTH.end,
  customer_id: "all", job_id: "all", vendor_id: "all",
  invoice_status: "all", cash_account: "all",
  year: new Date().getFullYear(),
};

export default function Reports() {
  useOpenedGoal("report_opened", true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [preset, setPreset] = useState("month");
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const [c, j, v] = await Promise.all([
        api.get("/lookups/customers"),
        api.get("/lookups/jobs"),
        api.get("/lookups/vendors"),
      ]);
      setCustomers(c.data); setJobs(j.data); setVendors(v.data);
      run({ ...EMPTY_FILTERS });
    })();
    // eslint-disable-next-line
  }, []);

  const run = async (f = filters) => {
    setLoading(true);
    const payload = {
      start_date: f.start_date || null,
      end_date: f.end_date || null,
      customer_id: f.customer_id !== "all" ? f.customer_id : null,
      job_id: f.job_id !== "all" ? f.job_id : null,
      vendor_id: f.vendor_id !== "all" ? f.vendor_id : null,
      invoice_status: f.invoice_status !== "all" ? f.invoice_status : null,
      cash_account: f.cash_account !== "all" ? f.cash_account : null,
      year: Number(f.year) || null,
    };
    try {
      const { data } = await api.post("/reports/generate", payload);
      setReport(data);
    } catch {
      toast.error("Rapor oluşturulamadı");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <StickyPageHead>
        <PageHeader title="Raporlar" subtitle="Müşteri kârlılığı, kasa analizi, aylık performans ve gider raporları" />
      </StickyPageHead>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList data-testid="reports-tabs">
          <TabsTrigger value="general" data-testid="reports-tab-general">Genel Rapor</TabsTrigger>
          <TabsTrigger value="vat" data-testid="reports-tab-vat">KDV Raporu</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="reports-tab-expenses">Gider Raporları</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6">

      <SectionCard title="Filtreler">
        <div className="p-5 space-y-4">
          <div className="flex flex-wrap gap-2" data-testid="report-presets">
            {PRESETS.map((p) => (
              <Button
                key={p.key}
                variant={preset === p.key ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setPreset(p.key);
                  if (p.key === "custom") return;
                  const r = presetRange(p.key);
                  setFilters((f) => ({ ...f, start_date: r.start, end_date: r.end }));
                }}
                className={preset === p.key ? "bg-slate-900 hover:bg-slate-800" : ""}
                data-testid={`report-preset-${p.key}`}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <FieldDate label="Başlangıç" value={filters.start_date} onChange={(v) => { setFilters({ ...filters, start_date: v }); setPreset("custom"); }} testId="report-start-date" />
          <FieldDate label="Bitiş" value={filters.end_date} onChange={(v) => { setFilters({ ...filters, end_date: v }); setPreset("custom"); }} testId="report-end-date" />
          <SelectField label="Müşteri" value={filters.customer_id} onChange={(v) => setFilters({ ...filters, customer_id: v })}
            options={[["all", "Tümü"], ...customers.map((c) => [c.id, c.name])]} testId="report-customer-select" />
          <SelectField label="İş" value={filters.job_id} onChange={(v) => setFilters({ ...filters, job_id: v })}
            options={[["all", "Tümü"], ...jobs.map((j) => [j.id, `${j.customer_name} — ${j.name}`])]} testId="report-job-select" />
          <SelectField label="Tedarikçi" value={filters.vendor_id} onChange={(v) => setFilters({ ...filters, vendor_id: v })}
            options={[["all", "Tümü"], ...vendors.map((v) => [v.id, v.name])]} testId="report-vendor-select" />
          <SelectField label="Fatura" value={filters.invoice_status} onChange={(v) => setFilters({ ...filters, invoice_status: v })}
            options={[["all", "Tümü"], ["invoiced", "Faturalı"], ["non_invoiced", "Faturasız"]]} testId="report-invoice-select" />
          <SelectField label="Kasa" value={filters.cash_account} onChange={(v) => setFilters({ ...filters, cash_account: v })}
            options={[["all", "Tümü"], ["company", "Şirket Kasası"], ["personal", "Şahsi Kasa"]]} testId="report-cash-select" />
          <div className="space-y-1.5">
            <Label className="text-xs">Yıl (Aylık Performans)</Label>
            <Input type="number" value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.target.value })} className="h-10 rounded-lg" data-testid="report-year-input" />
          </div>
          </div>
        </div>
        <div className="px-5 pb-5 flex justify-end gap-2">
          <Button variant="outline" onClick={() => { setPreset("month"); setFilters(EMPTY_FILTERS); run(EMPTY_FILTERS); }} data-testid="report-reset-button">Sıfırla</Button>
          <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => run()} disabled={loading} data-testid="report-generate-button">
            {loading ? "Hesaplanıyor..." : "Raporu Oluştur"}
          </Button>
        </div>
      </SectionCard>

      {report && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard label="Toplam Satış" value={fmtTRY(report.financial_summary.total_sales)} accent="emerald" testId="rep-total-sales" />
            <StatCard label="Toplam Satın Alma" value={fmtTRY(report.financial_summary.total_purchases)} accent="red" testId="rep-total-purchases" />
            <StatCard label="Toplam Gider" value={fmtTRY(report.financial_summary.total_expenses || 0)} accent="red" testId="rep-total-expenses" />
            <StatCard label="Net Kâr" value={fmtTRY(report.financial_summary.net_profit)}
              accent={report.financial_summary.net_profit >= 0 ? "emerald" : "red"} testId="rep-net-profit" />
          </div>

          {report.cash_flow && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard label="Toplam Tahsilat" value={fmtTRY(report.cash_flow.total_collections || 0)} accent="emerald" testId="rep-total-collections" />
              <StatCard label="Toplam Tedarikçi Ödemesi" value={fmtTRY(report.cash_flow.total_supplier_payments || 0)} accent="red" testId="rep-total-supplier-payments" />
              <StatCard label="Toplam Gider (Nakit)" value={fmtTRY(report.cash_flow.total_expenses || 0)} accent="red" testId="rep-cashflow-expenses" />
              <StatCard label="Net Nakit Hareketi" value={fmtTRY(report.cash_flow.net_cash_flow || 0)}
                accent={(report.cash_flow.net_cash_flow || 0) >= 0 ? "emerald" : "red"} testId="rep-net-cashflow" />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SectionCard title="Fatura Analizi">
              <div className="p-5 space-y-3 text-sm">
                <Row label="Faturalı Satış" value={fmtTRY(report.invoice_analysis.invoiced_sales)} accent="emerald" />
                <Row label="Faturasız Satış" value={fmtTRY(report.invoice_analysis.non_invoiced_sales)} />
                <Row label="Faturalı Satın Alma" value={fmtTRY(report.invoice_analysis.invoiced_purchases)} accent="red" />
                <Row label="Faturasız Satın Alma" value={fmtTRY(report.invoice_analysis.non_invoiced_purchases)} />
              </div>
            </SectionCard>
            <SectionCard title="Kasa Analizi">
              <div className="p-5 space-y-3 text-sm">
                <Row label="Şirket Kasası Tahsilat" value={fmtTRY(report.cash_analysis.company_income)} accent="emerald" />
                <Row label="Şirket Kasası Ödeme" value={fmtTRY(report.cash_analysis.company_expense)} accent="red" />
                <Row label="Şirket Kasası Bakiye" value={fmtTRY(report.cash_analysis.company_balance)} bold />
                <Row label="Şahsi Kasa Tahsilat" value={fmtTRY(report.cash_analysis.personal_income)} accent="emerald" />
                <Row label="Şahsi Kasa Ödeme" value={fmtTRY(report.cash_analysis.personal_expense)} accent="red" />
                <Row label="Şahsi Kasa Bakiye" value={fmtTRY(report.cash_analysis.personal_balance)} bold />
              </div>
            </SectionCard>
            <SectionCard title="Alacaklar">
              <div className="p-5 space-y-3 text-sm">
                <Row label="Toplam Alacak" value={fmtTRY(report.receivables.outstanding)} />
                <Row label="Vadesi Geçen Alacak" value={fmtTRY(report.receivables.overdue)} accent="red" bold />
              </div>
            </SectionCard>
            <SectionCard title="Borçlar">
              <div className="p-5 space-y-3 text-sm">
                <Row label="Toplam Borç" value={fmtTRY(report.payables.outstanding)} />
                <Row label="Vadesi Geçen Borç" value={fmtTRY(report.payables.overdue)} accent="red" bold />
              </div>
            </SectionCard>
          </div>

          <SectionCard title={`Aylık Performans — ${report.year}`} testId="rep-monthly-chart">
            <div className="p-5">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={report.monthly_performance}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" stroke="#94a3b8" axisLine={false} tickLine={false} />
                  <YAxis stroke="#94a3b8" axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                  <Tooltip formatter={(v) => fmtTRY(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="sales" name="Satış" fill="#97C216" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="purchases" name="Satın Alma" fill="#939AA6" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="profit" name="Kâr" fill="#055473" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>

          <SectionCard title="Müşteri Bazlı Karlılık" testId="rep-customer-profitability">
            {(!report.customer_profitability || (report.customer_profitability ?? []).length === 0) ? (
              <EmptyState title="Müşteri kârlılık verisi bulunamadı" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/50">
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <th className="px-5 py-3 font-semibold">Müşteri</th>
                      <th className="px-5 py-3 font-semibold text-right">Toplam Satış</th>
                      <th className="px-5 py-3 font-semibold text-right">Tahsil Edilen</th>
                      <th className="px-5 py-3 font-semibold text-right">Bekleyen Alacak</th>
                      <th className="px-5 py-3 font-semibold text-right">Maliyet</th>
                      <th className="px-5 py-3 font-semibold text-right">Net Kâr</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(report.customer_profitability ?? []).map((c) => (
                      <tr key={c.id} data-testid={`rep-customer-row-${c.id}`}>
                        <td className="px-5 py-3 font-medium">{c.name}</td>
                        <td className="px-5 py-3 text-right tabular-nums">{fmtTRY(c.sales)}</td>
                        <td className="px-5 py-3 text-right tabular-nums text-emerald-600">{fmtTRY(c.collected)}</td>
                        <td className="px-5 py-3 text-right tabular-nums">{fmtTRY(c.outstanding)}</td>
                        <td className="px-5 py-3 text-right tabular-nums text-red-600">{fmtTRY(c.costs)}</td>
                        <td className={`px-5 py-3 text-right tabular-nums font-semibold ${c.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtTRY(c.profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </>
      )}
        </TabsContent>

        <TabsContent value="expenses" className="space-y-6">
          <ExpenseReports />
        </TabsContent>

        <TabsContent value="vat" className="space-y-6">
          <VatReport />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FieldDate({ label, value, onChange, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} className="h-10 rounded-lg" data-testid={testId} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-10 rounded-lg" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          {(options ?? []).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Row({ label, value, accent, bold }) {
  const color = accent === "emerald" ? "text-emerald-600" : accent === "red" ? "text-red-600" : "text-slate-900";
  return (
    <div className="flex items-center justify-between">
      <div className="text-slate-600">{label}</div>
      <div className={`tabular-nums ${color} ${bold ? "font-semibold" : ""}`}>{value}</div>
    </div>
  );
}
